import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Updater, useImmer } from "use-immer";

import { ColorPrimary, IgButton, IgTypography } from "@intelligems/ig-theme";
import { Close, KeyboardArrowLeft } from "@mui/icons-material";
import { Dialog, Divider, Grid, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useGetExperiencesList } from "../../api/experiences-list/get-experiences-list";
import { useGetOrg } from "../../api/organizations/get-organization";
import { useIsAdmin } from "../../api/user-auths/is-admin";
import { AdvancedTestIconFilled } from "../../images/Icons/ContentTest/AdvancedTest/AdvancedTestIconFilled";
import { ContentTestIcon } from "../../images/Icons/ContentTest/ContentTestIcon";
import { OnsiteEditsTestIconFilled } from "../../images/Icons/ContentTest/OnsiteEditTest/OnsiteEditsTestIconFilled";
import { SplitUrlTestIconFilled } from "../../images/Icons/ContentTest/SplitUrlTest/SplitUrlTestIconFilled";
import { TemplateTestIconFilled } from "../../images/Icons/ContentTest/TemplateTest/TemplateTestIconFilled";
import { ThemeTestIconFilled } from "../../images/Icons/ContentTest/ThemeTest/ThemeTestIconFilled";
import { LockedIcon } from "../../images/Icons/LockedIcon";
import { OfferTestIcon } from "../../images/Icons/Offer/OfferTestIcon";
import { PriceTestIconFilled } from "../../images/Icons/PriceTest/PriceTestIconFilled";
import { ShippingTestIcon } from "../../images/Icons/ShippingTest/ShippingTestIcon";
import { EXPERIMENT_ROUTES, SETTINGS_ROUTES } from "../../routes/routes";
import { getDialog, setDialog } from "../../store/slices/global";
import theme from "../../theme";
import { ExperienceTypeListItem } from "../../types/experience";
import { testIdeas } from "../../utils/linkSheet";
import openNewTab from "../../utils/openNewTab";
import {
  checkAllScopes,
  checkAnyScope,
  checkBooleanScope,
} from "../ScopeWrappers/helpers";
import { ScopedComponentWithDisable } from "../ScopeWrappers/ScopedComponentWithDisable";
import {
  CREATE_CONTENT_EXPERIMENT_BUTTON,
  CREATE_DISCOUNT_EXPERIMENT_BUTTON,
  CREATE_PRICING_EXPERIMENT_BUTTON,
  CREATE_SHIPPING_EXPERIMENT_BUTTON,
} from "../ScopeWrappers/Scopes";

const PREFIX = "CreateExperimentDialog";

const classes = {
  buttonText: `${PREFIX}-buttonText`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.buttonText}`]: {
    letterSpacing: "0.5px",
  },
}));

const experienceTypeList: ExperienceTypeListItem[] = [
  {
    key: "pricing_experiment",
    name: "Pricing Test",
    features: CREATE_PRICING_EXPERIMENT_BUTTON,
    enabledIcon: (
      <PriceTestIconFilled sx={{ fontSize: "40px", marginTop: "-2px" }} />
    ),
    description:
      "Test the price of one or multiple products in your Shopify store.",
    link: `${EXPERIMENT_ROUTES.PriceTest}/?action=new`,
  },
  {
    key: "shipping_experiment",
    name: "Shipping Test",
    features: CREATE_SHIPPING_EXPERIMENT_BUTTON,
    enabledIcon: (
      <ShippingTestIcon sx={{ fontSize: "45px", marginTop: "-25px" }} />
    ),
    description:
      "Test the rates you charge for shipping, including free shipping thresholds.",
    link: `${EXPERIMENT_ROUTES.ShippingTest}/?action=new`,
  },
  {
    key: "content_experiment",
    name: "Content Test",
    features: CREATE_CONTENT_EXPERIMENT_BUTTON,
    enabledIcon: <ContentTestIcon sx={{ fontSize: "45px" }} />,
    description: "Test the display contents on your Shopify store.",
    link: `${EXPERIMENT_ROUTES.ContentTest}/?action=new`,
  },
  {
    key: "volume_discount_experiment",
    name: "Offers Test",
    features: CREATE_DISCOUNT_EXPERIMENT_BUTTON,
    enabledIcon: <OfferTestIcon sx={{ fontSize: "45px" }} />,
    description: "Test different offers on your Shopify store.",
    link: `${EXPERIMENT_ROUTES.OfferTest}/?action=new`,
  },
];

export const contentExperienceTypeList: ExperienceTypeListItem[] = [
  {
    key: "split_test",
    name: "Split URL Test",
    features: CREATE_CONTENT_EXPERIMENT_BUTTON,
    enabledIcon: <SplitUrlTestIconFilled sx={{ fontSize: "45px" }} />,
    description: "Test redirecting users to various site pages.",
    link: `${EXPERIMENT_ROUTES.ContentTest}/?action=new&type=content/url`,
  },
  {
    key: "onsite_test",
    name: "Onsite Edits",
    features: CREATE_CONTENT_EXPERIMENT_BUTTON,
    enabledIcon: <OnsiteEditsTestIconFilled sx={{ fontSize: "50px" }} />,
    description: "Test any element of your online store.",
    link: `${EXPERIMENT_ROUTES.ContentTest}/?action=new&type=content/onsiteEdits`,
  },
  {
    key: "template_test",
    name: "Template Test",
    features: CREATE_CONTENT_EXPERIMENT_BUTTON,
    enabledIcon: <TemplateTestIconFilled sx={{ fontSize: "45px" }} />,
    description: "Compare and test different Shopify templates.",
    link: `${EXPERIMENT_ROUTES.ContentTest}/?action=new&type=content/template`,
  },
  {
    key: "theme_test",
    name: "Theme Test",
    features: CREATE_CONTENT_EXPERIMENT_BUTTON,
    enabledIcon: <ThemeTestIconFilled sx={{ fontSize: "45px" }} />,
    description: "Compare and test different Shopify themes.",
    link: `${EXPERIMENT_ROUTES.ContentTest}/?action=new&type=content/theme`,
  },
  {
    key: "advanced_test",
    name: "Advanced",
    features: CREATE_CONTENT_EXPERIMENT_BUTTON,
    enabledIcon: <AdvancedTestIconFilled sx={{ fontSize: "45px" }} />,
    description: "Customize every element of your content test.",
    link: `${EXPERIMENT_ROUTES.ContentTest}/?action=new&type=content/advanced`,
  },
];

interface ExperienceTypeButtonProps {
  experienceType: ExperienceTypeListItem;
  selectedTestType: ExperienceTypeListItem | undefined;
  setSelectedTestType: Updater<ExperienceTypeListItem | undefined>;
  isDisabled?: boolean;
  setShowContentSelection: Updater<boolean>;
}

const ExperienceTypeButton = ({
  experienceType,
  isDisabled,
  selectedTestType,
  setSelectedTestType,
  setShowContentSelection,
}: ExperienceTypeButtonProps) => {
  const onClick = () => {
    if (!isDisabled) {
      setSelectedTestType(experienceType);
      if (experienceType.key === "content_experiment") {
        setShowContentSelection(true);
      }
    }
  };

  return (
    <Grid
      item
      onClick={onClick}
      sx={{
        minHeight: "100px",
        border:
          selectedTestType?.key === experienceType.key && !isDisabled
            ? `1px solid ${theme.palette.primary.main}`
            : "transparent",
        backgroundColor:
          selectedTestType?.key === experienceType.key && !isDisabled
            ? ColorPrimary.light5
            : "none",
        "&:hover": {
          backgroundColor: !isDisabled ? ColorPrimary.light5 : "none",
          cursor: !isDisabled ? "pointer" : "default",
        },
      }}
    >
      <Grid
        item
        container
        direction={"row"}
        sx={{ padding: "20px 63px 20px 63px" }}
        alignItems={"center"}
      >
        <Grid item sx={{ marginRight: "20px" }}>
          {isDisabled ? (
            <LockedIcon sx={{ fontSize: "37px", marginTop: "-20px" }} />
          ) : (
            experienceType.enabledIcon
          )}
        </Grid>
        <Grid item container direction={"column"} xs={10}>
          <IgTypography
            sx={{
              fontSize: "19px",
              fontWeight: 600,
              color: !isDisabled
                ? theme.palette.gray.main
                : theme.palette.disabled.main,
            }}
          >
            {experienceType.name}
          </IgTypography>
          <IgTypography
            variant={"body1"}
            sx={{
              width: "350px",
              color: !isDisabled
                ? theme.palette.gray.main
                : theme.palette.disabled.main,
            }}
          >
            {experienceType.description}
          </IgTypography>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const CreateExperienceTestDialog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const organization = useGetOrg();
  const experiencesList = useGetExperiencesList({ category: "experiment" });
  const dialog = useSelector(getDialog("createExperiment"));

  const isAdmin = useIsAdmin();
  const [selectedTestType, setSelectedTestType] = useImmer<
    ExperienceTypeListItem | undefined
  >(undefined);

  const [showContentSelection, setShowContentSelection] =
    useImmer<boolean>(false);

  useEffect(() => {
    setShowContentSelection(dialog.props?.showContentSection || false);
  }, [dialog.props?.showContentSection]);

  const filterKeys = dialog.props?.filterKeys || [];
  const hideBackButton = dialog.props?.hideBackButton || false;
  const hideInspiration = dialog.props?.hideInspiration || false;

  const handleClose = () => {
    dispatch(
      setDialog({ dialog: "createExperiment", open: false, props: undefined })
    );
    setSelectedTestType(undefined);
  };

  const doesHaveAccess = () => {
    const featureAccess: boolean[] = [];
    experienceTypeList.forEach((type) => {
      const featureTypeAccess: boolean[] = [];
      type.features.forEach((feature) => {
        featureTypeAccess.push(
          checkBooleanScope(feature, organization.data.org.configurations)
        );
      });
      featureAccess.push(checkAllScopes(featureTypeAccess));
    });
    return isAdmin || featureAccess.every((feature) => feature);
  };

  const onCreateClick = () => {
    if (selectedTestType?.key === "content_experiment") {
      // set the dialog to show content types
      setShowContentSelection(true);
    } else {
      handleClose();
      navigate(selectedTestType!.link, {
        state: {
          name: dialog.props?.name,
          description: dialog.props?.description,
        },
      });
    }
  };

  const items = useMemo(() => {
    const _items = showContentSelection
      ? contentExperienceTypeList
      : experienceTypeList;

    if (!filterKeys.length) {
      return _items;
    }

    return _items.filter((i) => filterKeys.includes(i.key));
  }, [filterKeys.length, showContentSelection]);

  const canCreateShippingTest = useMemo(() => {
    if (experiencesList.isSuccess) {
      return (
        experiencesList.data.experiencesList.filter(
          (exp) => exp.testTypes.hasTestShipping && exp.status !== "ended"
        ).length === 0
      );
    } else return true;
  }, [experiencesList]);

  if (experiencesList.isSuccess && dialog.isOpen) {
    return (
      <StyledDialog
        fullWidth
        onClose={() =>
          dispatch(setDialog({ dialog: "createExperiment", open: false }))
        }
        open={dialog.isOpen}
        aria-labelledby="create-experiment-dialogBox"
        aria-describedby="choose-experiment-type"
        PaperProps={{
          style: {
            borderRadius: 15,
            maxWidth: "577px",
            paddingBottom: "32px",
          },
        }}
      >
        <Grid container direction={"column"}>
          <Grid
            item
            container
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            sx={{ padding: "20px 20px 10px 20px" }}
          >
            <Grid item xs={1}>
              {showContentSelection && !hideBackButton && (
                <IgButton
                  onClick={() => {
                    setSelectedTestType(undefined);
                    setShowContentSelection(false);
                  }}
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    letterSpacing: "0.15px",
                  }}
                >
                  <KeyboardArrowLeft />
                  Back
                </IgButton>
              )}
            </Grid>
            <Grid item>
              <IgTypography
                sx={{
                  color: theme.palette.gray.main,
                  fontSize: "22px",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                }}
              >
                Choose {showContentSelection ? "Content" : "Your"} Test Type
              </IgTypography>
            </Grid>
            <Grid item xs={1}>
              <IconButton onClick={handleClose}>
                <Close color={"disabled"} />
              </IconButton>
            </Grid>
          </Grid>
          <Grid item container justifyContent={"center"}>
            <Divider
              sx={{
                "&.MuiDivider-root.MuiDivider-fullWidth": {
                  width: "515px",
                },
              }}
            />
          </Grid>
          <Grid item container direction={"column"}>
            {items.map((test) => (
              <Grid item key={test.key}>
                <ScopedComponentWithDisable
                  adminDisable={!canCreateShippingTest}
                  isEnabled={
                    test.key === "shipping_experiment"
                      ? checkAllScopes([
                          checkAnyScope(
                            test.features.map((feature) =>
                              checkBooleanScope(
                                feature,
                                organization.data.org.configurations
                              )
                            )
                          ),
                          canCreateShippingTest,
                        ])
                      : test.key === "volume_discount_experiment"
                        ? checkAllScopes(
                            test.features.map((feature) =>
                              checkBooleanScope(
                                feature,
                                organization.data.org.configurations
                              )
                            )
                          )
                        : checkAnyScope(
                            test.features.map((feature) =>
                              checkBooleanScope(
                                feature,
                                organization.data.org.configurations
                              )
                            )
                          )
                  }
                  customDisabledTooltipMessage={
                    test.key === "shipping_experiment" && !canCreateShippingTest
                      ? "Please delete or end your existing shipping test."
                      : "Please upgrade your plan to unlock this experiment."
                  }
                  debugId={`create-${test.key}-experiment-modal-button`}
                  customComponentWhenDisabled={
                    <ExperienceTypeButton
                      experienceType={test}
                      isDisabled={true}
                      setSelectedTestType={setSelectedTestType}
                      selectedTestType={selectedTestType}
                      setShowContentSelection={setShowContentSelection}
                    />
                  }
                >
                  <ExperienceTypeButton
                    experienceType={test}
                    setSelectedTestType={setSelectedTestType}
                    selectedTestType={selectedTestType}
                    setShowContentSelection={setShowContentSelection}
                  />
                </ScopedComponentWithDisable>
                <Grid item container justifyContent={"center"}>
                  <Divider
                    sx={{
                      "&.MuiDivider-root.MuiDivider-fullWidth": {
                        width: "515px",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Grid
            item
            container
            direction={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            rowSpacing={2}
            sx={{ marginTop: "10px" }}
          >
            {hideInspiration ? null : (
              <Grid item>
                <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
                  Looking for inspiration? See our{" "}
                  <IgTypography
                    variant={"body2"}
                    sx={{ fontWeight: 600 }}
                    onClick={() => {
                      openNewTab(testIdeas);
                    }}
                    igStyle={"link"}
                  >
                    list of test ideas.
                  </IgTypography>
                </IgTypography>
              </Grid>
            )}
            {!doesHaveAccess() && (
              <Grid item>
                <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
                  <IgTypography
                    igStyle={"link"}
                    variant={"body2"}
                    sx={{ fontWeight: 600 }}
                    onClick={() => {
                      navigate(SETTINGS_ROUTES.Settings);
                      handleClose();
                    }}
                  >
                    Upgrade your plan{" "}
                  </IgTypography>
                  if you're interested in unlocking these tests.
                </IgTypography>
              </Grid>
            )}
            <Grid item>
              <IgButton
                onClick={onCreateClick}
                disabled={
                  !selectedTestType ||
                  (selectedTestType.key === "content_experiment" &&
                    showContentSelection)
                }
                size={"large"}
                variant={"contained"}
                className={classes.buttonText}
              >
                Create Test
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      </StyledDialog>
    );
  }
  return null;
};
